import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { green, grey, red, yellow } from '@mui/material/colors';

interface HealthAdjustModalProps {
  open: boolean;
  currentHealth: number;
  maxHealth: number;
  onClose: () => void;
  onApply: (nextHealth: number) => void;
}

   // Returns a red shade that darkens as hp decreases (red[300] at full, red[900] at 1).
export const getBarColor = (hp: number, maxHealth: number): string => {
  const shades = ["#440000", "#550000", "#660000", "#770000", "#880000", "#990000", "#AA0000", "#BB0000", "#CC0000", "#DD0000", "#EE0000", "#FF0000"] as const;
  if (maxHealth <= 0) return yellow[500];
  const ratio = (hp) / (maxHealth); // 0 at hp=1, 1 at hp=maxHealth
  const idx = Math.round(ratio * (shades.length - 1));
  return shades[idx];
};

export const HealthAdjustModal: React.FC<HealthAdjustModalProps> = ({
  open,
  currentHealth,
  maxHealth,
  onClose,
  onApply,
}) => {
  const [draftHealth, setDraftHealth] = useState(currentHealth);
  const isDragging = useRef(false);

  const handleBarPointerDown = (hp: number) => {
    isDragging.current = true;
    setDraftHealth(hp);
  };

  const handleBarPointerEnter = (hp: number) => {
    if (isDragging.current) setDraftHealth(hp);
  };

  const handlePointerUp = () => {
    isDragging.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const barEl = el?.closest('[data-hp]');
    if (barEl) {
      const hp = parseInt(barEl.getAttribute('data-hp') ?? '', 10);
      if (!isNaN(hp)) setDraftHealth(hp);
    }
  };

  useEffect(() => {
    if (open) {
      setDraftHealth(currentHealth);
    }
  }, [open, currentHealth]);

  const delta = draftHealth - currentHealth;

  const deltaColor =
    delta > 0 ? green[600] : delta < 0 ? red[700] : grey[500];

  const deltaLabel =
    delta > 0 ? `${delta}` : delta < 0 ? `${Math.abs(delta)}` : '';

  // Bars rendered top-to-bottom represent maxHealth down to 1.
  // We produce an array [maxHealth, maxHealth-1, ..., 1] for display order.
  const barIndices = Array.from({ length: maxHealth + 1 }, (_, i) => maxHealth - i);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Adjust Health</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>

          {/* Delta column */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 48,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: deltaColor,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {deltaLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {delta > 0 ? 'Healing' : delta < 0 ? 'Damage' : ''}
            </Typography>
          </Box>

          {/* Bar column */}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1, px: 1.5 }}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchMove={handleTouchMove}
            onTouchEnd={handlePointerUp}
          >
            {barIndices.map((hp) => {
              const isFilled = hp <= draftHealth;
              const isSelected = hp === draftHealth;
              const wasCurrentHealth = hp === currentHealth;

              return (
                // data-hp marks this row (and its children) as exempt from the
                // high-contrast CSS override — see theme/highContrast.css.
                <Box key={hp} data-hp={hp} sx={{ position: 'relative' }}>
                  {wasCurrentHealth && (
                    <>
                      <Box sx={{
                        position: 'absolute',
                        left: -10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 8,
                        height: 2,
                        backgroundColor: grey[900],
                      }} />
                      <Box sx={{
                        position: 'absolute',
                        right: -10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 8,
                        height: 2,
                        backgroundColor: grey[900],
                      }} />
                    </>
                  )}
                  <Box
                    data-hp={hp}
                    onMouseDown={() => handleBarPointerDown(hp)}
                    onMouseEnter={() => handleBarPointerEnter(hp)}
                    onTouchStart={(e) => { e.preventDefault(); handleBarPointerDown(hp); }}
                    sx={{
                      height: 28,
                      borderRadius: 1,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      backgroundColor: isFilled ? getBarColor(hp, maxHealth) : grey[200],
                      filter: isSelected ? 'brightness(1.3)' : 'none',
                      transition: 'background-color 0.1s, filter 0.1s',
                      '&:hover': {
                        filter: 'brightness(1.2)',
                        backgroundColor: !isFilled ? grey[300] : undefined,
                      },
                      userSelect: 'none',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: isFilled ? 'white' : grey[600],
                        fontWeight: isSelected ? 700 : 400,
                        lineHeight: 1,
                      }}
                    >
                      {hp == 0 ? "Incapacitated" : hp}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onApply(draftHealth)}
          disabled={delta === 0}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
